/**
 * HR Config Service — konfigurowalny system HR (API backend).
 *
 * Zarządza: kategoriami wniosków, typami wniosków, pulami saldowymi, szablonami.
 * Dane pobierane z backendu przez apiService.
 */

import apiService from "../api/apiService";

// ─── Exported Constants (used by consuming components) ──────────────
export const ALL_EMPLOYMENT_TYPES = [
    { value: "umowa_o_prace", label: "Umowa o pracę" },
    { value: "umowa_zlecenie", label: "Umowa zlecenie" },
    { value: "umowa_o_dzielo", label: "Umowa o dzieło" },
    { value: "b2b", label: "B2B" },
];

export const ALL_WORK_DIMENSIONS = [
    { value: "full", label: "Pełny etat (8h)", hours: 8 },
    { value: "1/2", label: "1/2 etatu (4h)", hours: 4 },
    { value: "1/4", label: "1/4 etatu (2h)", hours: 2 },
    { value: "1/8", label: "1/8 etatu (1h)", hours: 1 },
];

// ─── Pool <-> Leave-limit key mappings ──────────────────────────────
export const POOL_TO_LIMIT_KEY = {
    vacationDays: "vacation",
    onDemandDays: "onDemand",
    childCareDays: "childCare",
    forceMajeure: "forceMajeure",
    careDays: "careLeave",
    remoteOccasional: "occasionalRemote",
};

export const LIMIT_KEY_TO_POOL = {
    vacation: "vacationDays",
    onDemand: "onDemandDays",
    childCare: "childCareDays",
    forceMajeure: "forceMajeure",
    careLeave: "careDays",
    occasionalRemote: "remoteOccasional",
};

export const POOL_EMOJI = {
    vacationDays: "\u{1F3D6}\u{FE0F}",
    onDemandDays: "\u26A1",
    childCareDays: "\u{1F476}",
    forceMajeure: "\u{1F525}",
    careDays: "\u{1F91D}",
    remoteOccasional: "\u{1F3E0}",
};

// ─── Old -> New category mapping (for existing requests) ────────────
const CATEGORY_MIGRATION_MAP = {
    TIME_OFF: "CURRENT_WORK",
    DOCUMENTS_OTHER: "CURRENT_WORK",
    PERSONAL_FINANCE: "FINANCE_SOCIAL",
    REMOTE_WORK: "REMOTE_WORK",
};

// ─── Map backend DTO to frontend shape ─────────────────────────────
const mapTypeDto = (dto) => {
    if (!dto) return dto;
    const mapped = { ...dto };

    // Parse fieldsJson → fields (array)
    if (typeof mapped.fieldsJson === "string") {
        try { mapped.fields = JSON.parse(mapped.fieldsJson); } catch { mapped.fields = []; }
    }
    if (!mapped.fields) mapped.fields = [];

    // Parse availableForJson → availableFor (object)
    if (typeof mapped.availableForJson === "string") {
        try { mapped.availableFor = JSON.parse(mapped.availableForJson); } catch { mapped.availableFor = {}; }
    }
    if (!mapped.availableFor) mapped.availableFor = null;

    // Parse templateJson → template (object)
    if (typeof mapped.templateJson === "string" && mapped.templateJson) {
        try { mapped.template = JSON.parse(mapped.templateJson); } catch { mapped.template = null; }
    }
    if (mapped.template === undefined) mapped.template = null;

    // Parse approvalStepsJson → approvalSteps (array [{order,role,mode,label}])
    if (typeof mapped.approvalStepsJson === "string" && mapped.approvalStepsJson) {
        try { mapped.approvalSteps = JSON.parse(mapped.approvalStepsJson); } catch { mapped.approvalSteps = []; }
    }
    if (!Array.isArray(mapped.approvalSteps)) mapped.approvalSteps = [];

    // Parse relatedTypeCodesJson → relatedTypeCodes (lista kodów typów proponowanych do złożenia razem)
    if (typeof mapped.relatedTypeCodesJson === "string" && mapped.relatedTypeCodesJson) {
        try { mapped.relatedTypeCodes = JSON.parse(mapped.relatedTypeCodesJson); } catch { mapped.relatedTypeCodes = []; }
    }
    if (!Array.isArray(mapped.relatedTypeCodes)) mapped.relatedTypeCodes = [];

    // limitPoolCode → limitPool
    if (mapped.limitPoolCode && !mapped.limitPool) {
        mapped.limitPool = mapped.limitPoolCode;
    }

    // category alias
    if (mapped.categoryId && !mapped.category) {
        mapped.category = mapped.categoryId;
    }

    return mapped;
};

// ═══════════════════════════════════════════════════════════════════
//                      PUBLIC API
// ═══════════════════════════════════════════════════════════════════

const hrConfigService = {

    // ── CATEGORIES ──────────────────────────────────────────────────

    async getCategories() {
        try {
            const response = await apiService.getHrCategories();
            const categories = response.data || [];
            return [...categories].sort((a, b) => a.order - b.order);
        } catch (error) {
            console.error("hrConfigService.getCategories error:", error);
            return [];
        }
    },

    async getCategory(id) {
        try {
            const categories = await this.getCategories();
            return categories.find((c) => c.id === id) || null;
        } catch (error) {
            console.error("hrConfigService.getCategory error:", error);
            return null;
        }
    },

    async saveCategory(category) {
        try {
            if (category.id) {
                // Check if exists — update
                const existing = await this.getCategory(category.id);
                if (existing) {
                    await apiService.updateHrCategory(category.id, category);
                } else {
                    const response = await apiService.createHrCategory(category);
                    return response.data;
                }
            } else {
                const response = await apiService.createHrCategory(category);
                return response.data;
            }
            return await this.getCategories();
        } catch (error) {
            console.error("hrConfigService.saveCategory error:", error);
            throw error;
        }
    },

    async deleteCategory(id) {
        try {
            await apiService.deleteHrCategory(id);
            return true;
        } catch (error) {
            console.error("hrConfigService.deleteCategory error:", error);
            throw error;
        }
    },

    // ── REQUEST TYPES ───────────────────────────────────────────────

    async getRequestTypes() {
        try {
            const response = await apiService.getHrRequestTypes();
            const types = response.data || [];
            return types
                .filter((t) => t.isActive !== false)
                .map(mapTypeDto);
        } catch (error) {
            console.error("hrConfigService.getRequestTypes error:", error);
            return [];
        }
    },

    async getAllRequestTypes() {
        try {
            const response = await apiService.getHrRequestTypes();
            const types = response.data || [];
            return types.map(mapTypeDto);
        } catch (error) {
            console.error("hrConfigService.getAllRequestTypes error:", error);
            return [];
        }
    },

    async getRequestType(idOrCode) {
        try {
            const types = await this.getAllRequestTypes();
            // Szukaj po id (GUID) lub po code (np. "VACATION")
            return types.find((t) => t.id === idOrCode || t.code === idOrCode) || null;
        } catch (error) {
            console.error("hrConfigService.getRequestType error:", error);
            return null;
        }
    },

    async getTypesByCategory(categoryId) {
        try {
            const types = await this.getRequestTypes();
            return types
                .filter((t) => t.categoryId === categoryId)
                .sort((a, b) => a.order - b.order);
        } catch (error) {
            console.error("hrConfigService.getTypesByCategory error:", error);
            return [];
        }
    },

    // Jak getTypesByCategory, ale ZAWIERA typy ukryte (isActive=false) — do panelu admina (widok „Ukryte" + przywracanie).
    async getAllTypesByCategory(categoryId) {
        try {
            const types = await this.getAllRequestTypes();
            return types
                .filter((t) => t.categoryId === categoryId)
                .sort((a, b) => a.order - b.order);
        } catch (error) {
            console.error("hrConfigService.getAllTypesByCategory error:", error);
            return [];
        }
    },

    async saveRequestType(type) {
        try {
            // Ensure JSON strings for backend (backend expects fieldsJson/availableForJson/templateJson as strings)
            const payload = { ...type };
            if (Array.isArray(payload.fields)) {
                payload.fieldsJson = JSON.stringify(payload.fields);
            }
            if (payload.availableFor && typeof payload.availableFor === "object") {
                payload.availableForJson = JSON.stringify(payload.availableFor);
            }
            if (payload.template && typeof payload.template === "object") {
                payload.templateJson = JSON.stringify(payload.template);
            }
            // approvalSteps (array) → approvalStepsJson (string) — ścieżka akceptacji
            if (Array.isArray(payload.approvalSteps)) {
                payload.approvalStepsJson = JSON.stringify(payload.approvalSteps);
            }
            // relatedTypeCodes (array) → relatedTypeCodesJson (string). UWAGA na zapadkę serwera:
            // null/puste NIE zeruje zapisanej listy — świadome wyczyszczenie wymaga jawnego "[]",
            // dlatego serializujemy ZAWSZE, gdy edytor trzyma tablicę (także pustą).
            if (Array.isArray(payload.relatedTypeCodes)) {
                payload.relatedTypeCodesJson = JSON.stringify(payload.relatedTypeCodes);
            }
            if (payload.limitPool && !payload.limitPoolCode) {
                payload.limitPoolCode = payload.limitPool;
            }
            // FIX audytu (H7): CreateHrRequestTypeDto.Code jest wymagane — dla NOWEGO typu wyślij kod (slug z etykiety
            // = payload.id budowane w edytorze). Dawniej brak 'code' → automatyczne 400 i nie dało się utworzyć typu.
            if (!payload.code) payload.code = payload.id;
            // FIX audytu 2026-08-29 (H-05): edytor typu trzyma kategorię w polu `category`, a CreateHrRequestTypeDto
            // oczekuje `categoryId` (nienullowalny Guid). Bez tego mapowania nowy typ szedł z Guid.Empty → 500 na FK
            // albo typ-sierota niewidoczny w drzewie i w kreatorze pracownika.
            if (!payload.categoryId && payload.category) {
                payload.categoryId = payload.category;
            }

            if (type.id) {
                const existing = await this.getRequestType(type.id);
                if (existing) {
                    await apiService.updateHrRequestType(type.id, payload);
                    return await this.getRequestType(type.id);
                } else {
                    const response = await apiService.createHrRequestType(payload);
                    return response.data;
                }
            } else {
                const response = await apiService.createHrRequestType(payload);
                return response.data;
            }
        } catch (error) {
            console.error("hrConfigService.saveRequestType error:", error);
            throw error;
        }
    },

    // ── Symulacja ścieżki akceptacji („Pokaż na przykładzie") ──
    async simulateApprovalPath(payload) {
        const res = await apiService.simulateHrApprovalPath(payload);
        return res.data;
    },

    // ── Reset wniosku jednorazowego (HR odblokowuje ponowne złożenie) ──
    async resetOneTime(typeCode, userId) {
        const res = await apiService.resetHrOneTime({ typeCode, userId: Number(userId) });
        return res.data; // { reset: <liczba odblokowanych wnioskow> }
    },

    // ── Biblioteka klauzul (pouczenia do wzorów pism) ──
    async getClauseDefinitions(includeInactive = false) {
        try {
            const res = await apiService.getHrClauseDefinitions(includeInactive);
            return res.data || [];
        } catch {
            return [];
        }
    },
    async saveClauseDefinition(payload) {
        const res = await apiService.saveHrClauseDefinition(payload);
        return res.data;
    },
    async deleteClauseDefinition(id) {
        await apiService.deleteHrClauseDefinition(id);
    },

    // ── Warianty umów (baza urlopu per forma zatrudnienia) ──
    async getContractVariants(includeInactive = false) {
        try {
            const res = await apiService.getHrContractVariants(includeInactive);
            return res.data || [];
        } catch {
            return [];
        }
    },
    async saveContractVariant(payload) {
        const res = await apiService.saveHrContractVariant(payload);
        return res.data;
    },
    async deleteContractVariant(id) {
        await apiService.deleteHrContractVariant(id);
    },
    // [S-6] Zakładki per forma zatrudnienia (Moja ścieżka kariery / Moje cele) — macierz i polityka „bez formy zatrudnienia”.
    async getContractGates() {
        const res = await apiService.getHrContractGates();
        return res.data || null;
    },
    async saveContractGates(payload) {
        const res = await apiService.saveHrContractGates(payload);
        return res.data || null;
    },

    // ── Biblioteka nazwanych ścieżek akceptacji ──
    async getApprovalPathPresets(includeInactive = false) {
        try {
            const res = await apiService.getHrApprovalPathPresets(includeInactive);
            return (res.data || []).map((p) => {
                let steps = [];
                try { steps = p.stepsJson ? JSON.parse(p.stepsJson) : []; } catch { steps = []; }
                return { ...p, steps };
            });
        } catch {
            return [];
        }
    },
    // Zwraca { saved, duplicate, preset, existingDuplicate, message }
    async saveApprovalPathPreset({ id, name, description, steps }) {
        const res = await apiService.saveHrApprovalPathPreset({
            id: id || null,
            name,
            description: description || null,
            stepsJson: JSON.stringify(steps || []),
        });
        return res.data;
    },
    async deleteApprovalPathPreset(id) {
        await apiService.deleteHrApprovalPathPreset(id);
    },

    // ── Katalog pól formularza (definiowany w systemie) ──
    async getFieldDefinitions(includeInactive = false) {
        try {
            const response = await apiService.getHrFieldDefinitions(includeInactive);
            const list = (response.data || []).map((f) => {
                let options = [];
                try { options = f.optionsJson ? JSON.parse(f.optionsJson) : []; } catch { options = []; }
                return { ...f, options };
            });
            return list;
        } catch (err) {
            console.error("hrConfigService.getFieldDefinitions error:", err);
            return [];
        }
    },
    async saveFieldDefinition(def) {
        const payload = { ...def };
        if (Array.isArray(def.options)) payload.optionsJson = JSON.stringify(def.options);
        if (def.id) { await apiService.updateHrFieldDefinition(def.id, payload); return true; }
        const res = await apiService.createHrFieldDefinition(payload);
        return res.data;
    },
    async deleteFieldDefinition(id) {
        await apiService.deleteHrFieldDefinition(id);
        return true;
    },

    // Historia wersji typu wniosku (podglad + przywrocenie)
    async getRequestTypeVersions(typeId) {
        try {
            const response = await apiService.getHrRequestTypeVersions(typeId);
            return response.data || [];
        } catch (err) {
            console.error("hrConfigService.getRequestTypeVersions error:", err);
            return [];
        }
    },

    async restoreRequestTypeVersion(typeId, version) {
        await apiService.restoreHrRequestTypeVersion(typeId, version);
        return true;
    },

    async deleteRequestType(id) {
        try {
            await apiService.deleteHrRequestType(id);
            return true;
        } catch (error) {
            console.error("hrConfigService.deleteRequestType error:", error);
            throw error;
        }
    },

    /**
     * [brief właściciela 2026-08-30, pkt 6] Kategorie z typami, KTÓRE PRACOWNIKOWI
     * PRZYSŁUGUJĄ — źródło listy w kreatorze „Nowy wniosek".
     *
     * BYŁO ŹLE: front pobierał komplet typów (`GET /hr-config/request-types`) i zawężał
     * listę u siebie po formie zatrudnienia i wymiarze etatu — po to tylko, żeby typ
     * WYSZARZYĆ. Sald, jednorazowości ani typów managerskich nie sprawdzał wcale,
     * a wyszukiwarka obchodziła nawet to zawężenie (audyt M-12).
     * TERAZ: liczy serwer (`HrTypeAvailability`), tą samą funkcją, którą sprawdza POST.
     * Kategorii bez ani jednego dostępnego typu backend w ogóle nie zwraca.
     *
     * Awaria API zwraca `null` (a nie `[]`), żeby ekran odróżnił „nic Ci nie przysługuje"
     * od „nie udało się pobrać" — pusta lista po awarii kłamałaby w obie strony.
     */
    async getAvailableTypesForUser(forUserId = null) {
        try {
            const res = await apiService.getHrAvailableTypes(forUserId);
            return (res.data || []).map((cat) => ({
                ...cat,
                types: (cat.types || []).map(mapTypeDto),
            }));
        } catch (error) {
            console.error("hrConfigService.getAvailableTypesForUser error:", error);
            return null;
        }
    },

    /** [brief pkt 3] Spółki, do których zalogowany może skierować wniosek. */
    async getMyCompanies() {
        try {
            const res = await apiService.getHrMyCompanies();
            return Array.isArray(res.data) ? res.data : [];
        } catch (error) {
            console.error("hrConfigService.getMyCompanies error:", error);
            return [];
        }
    },

    /** [brief pkt A] Słownik właściwości pola (zakresy osoby, semantyka, pola pochodne). */
    async getFieldSchema() {
        try {
            const res = await apiService.getHrFieldSchema();
            return res.data || null;
        } catch (error) {
            console.error("hrConfigService.getFieldSchema error:", error);
            return null;
        }
    },

    /* USUNIĘTE (brief właściciela 2026-08-30, pkt 6 + audyt M-12): getAvailableTypes,
       getAvailableTypesGrouped, getAllTypesWithAvailability, getAllTypesGroupedWithAvailability.
       To był GATING PO STRONIE PRZEGLĄDARKI: serwer przysyłał komplet 44 typów, a te funkcje
       zawężały listę u siebie — wyłącznie po formie zatrudnienia i wymiarze etatu, i wyłącznie
       po to, by typ wyszarzyć. Sald, jednorazowości, ukrycia typu ani rangi managerskiej nie
       sprawdzały wcale, a wyszukiwarka kreatora omijała nawet to zawężenie, bo szukała po
       PEŁNEJ liście. Zawężenie liczy dziś serwer — patrz getAvailableTypesForUser() wyżej. */

    // ── MANAGER-ONLY TYPES ─────────────────────────────────────────

    /**
     * Zwraca aktywne typy wniosków dostępne TYLKO dla przełożonych.
     */
    async getManagerOnlyTypes() {
        try {
            const types = await this.getRequestTypes();
            return types
                .filter((t) => t.managerOnly === true)
                .sort((a, b) => a.order - b.order);
        } catch (error) {
            console.error("hrConfigService.getManagerOnlyTypes error:", error);
            return [];
        }
    },

    // ── BALANCE POOLS ───────────────────────────────────────────────

    async getBalancePools() {
        try {
            const response = await apiService.getHrBalancePools();
            return response.data || [];
        } catch (error) {
            console.error("hrConfigService.getBalancePools error:", error);
            return [];
        }
    },

    async getBalancePool(idOrCode) {
        try {
            const pools = await this.getBalancePools();
            return pools.find((p) => p.id === idOrCode || p.code === idOrCode) || null;
        } catch (error) {
            console.error("hrConfigService.getBalancePool error:", error);
            return null;
        }
    },

    async saveBalancePool(pool) {
        try {
            // FIX audytu ([37]): CreateHrBalancePoolDto.Code jest wymagane — dla nowej puli wyślij kod (slug = pool.id
            // budowane w managerze). Dawniej brak 'code' → 400 i pula się nie tworzyła.
            if (!pool.code) pool.code = pool.id;
            if (pool.id) {
                const existing = await this.getBalancePool(pool.id);
                if (existing) {
                    await apiService.updateHrBalancePool(pool.id, pool);
                } else {
                    const response = await apiService.createHrBalancePool(pool);
                    return response.data;
                }
            } else {
                const response = await apiService.createHrBalancePool(pool);
                return response.data;
            }
            return await this.getBalancePools();
        } catch (error) {
            console.error("hrConfigService.saveBalancePool error:", error);
            throw error;
        }
    },

    async deleteBalancePool(id) {
        try {
            await apiService.deleteHrBalancePool(id);
            return true;
        } catch (error) {
            console.error("hrConfigService.deleteBalancePool error:", error);
            throw error;
        }
    },

    // ── TEMPLATES ───────────────────────────────────────────────────

    async getTemplate(typeId) {
        const type = await this.getRequestType(typeId);
        return type?.template || null;
    },

    // USUNIETE (brief wlasciciela 2026-08-29): saveTemplate() i getTemplateVariables().
    // saveTemplate zapisywal wzor w wycofanym formacie tekstowym v1, ktorego generator dokumentow
    // nie renderuje — byl no-opem, a „naprawiony" kasowalby dzialajacy wzor blokowy. getTemplateVariables
    // zwracalo wlasna, rozjezdzajaca sie liste zmiennych. Jedynym zrodlem obu jest teraz kreator pisma
    // (HrDocumentBuilder + hrTemplateModel), ktory zapisuje typ wniosku przez saveRequestType.

    // ── APPROVAL FLOW HELPERS (pure, synchronous) ───────────────────

    getApprovalFlags(approvalFlow) {
        switch (approvalFlow) {
            case "manager":
                return { requiresManager: true, requiresHr: false, requiresDirector: false };
            case "manager_hr":
                return { requiresManager: true, requiresHr: true, requiresDirector: false };
            case "director_hr":
                return { requiresManager: false, requiresHr: true, requiresDirector: true };
            case "manager_director_hr":
                return { requiresManager: true, requiresHr: true, requiresDirector: true };
            case "hr_only":
                return { requiresManager: false, requiresHr: true, requiresDirector: false };
            case "auto":
                return { requiresManager: false, requiresHr: false, requiresDirector: false };
            default:
                return { requiresManager: true, requiresHr: true, requiresDirector: false };
        }
    },

    // ── WITHDRAWAL TYPE (idempotent ensure) ────────────────────────

    /**
     * Tworzy typ "Wycofanie wniosku" jeśli jeszcze nie istnieje.
     * Idempotentna — jeśli typ już jest w bazie, zwraca go bez zmian.
     */
    async ensureWithdrawalType() {
        try {
            const existing = await this.getRequestType("WITHDRAWAL");
            if (existing) return existing;

            const categories = await this.getCategories();
            // Szukaj kategorii "Korekta / Wycofanie" lub pierwszej dostępnej
            const targetCat = categories.find(c =>
                c.label?.toLowerCase().includes("korekta") ||
                c.label?.toLowerCase().includes("wycofanie")
            ) || categories[0];
            if (!targetCat) return null;

            const type = {
                code: "WITHDRAWAL",
                categoryId: targetCat.id,
                label: "Wycofanie wniosku",
                shortLabel: "Wycofanie",
                description: "Wniosek o wycofanie wcześniej zatwierdzonego wniosku (do 14 dni)",
                icon: "FiRotateCcw",
                approvalFlow: "manager_hr",
                order: 99,
                isActive: true,
                managerOnly: false,
                fields: [
                    { name: "originalRequestId", type: "hidden", label: "ID wniosku", required: true },
                    { name: "originalRequestNumber", type: "text", label: "Nr oryginalnego wniosku", required: true, readonly: true },
                    { name: "originalTypeLabel", type: "text", label: "Typ oryginalnego wniosku", required: true, readonly: true },
                    { name: "originalDateRange", type: "text", label: "Okres oryginalnego wniosku", required: true, readonly: true },
                    { name: "reason", type: "textarea", label: "Powód wycofania", required: true },
                ],
                availableFor: {},
            };

            await this.saveRequestType(type);
            return await this.getRequestType("WITHDRAWAL");
        } catch (err) {
            console.error("[hrConfigService] ensureWithdrawalType error:", err);
            return null;
        }
    },

    // ── CATEGORY MIGRATION MAP (publicly accessible) ────────────────
    CATEGORY_MIGRATION_MAP,
};

export default hrConfigService;
